/**
 * THE WORKER MESSAGE INTERFACE — contracts/adapter.md §3, typed.
 *
 * This file is the one UI builds against. It is deliberately in `src/core` and touches no
 * browser API at all, so both sides of `postMessage` import the SAME types and a mismatch is
 * a compile error rather than a runtime shrug.
 *
 * Every message is `{ kind, reqId, ... }`. `reqId` is a monotonically increasing integer
 * minted by the MAIN THREAD; every reply carries the `reqId` it answers. Fire-and-forget
 * messages use `reqId: 0`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────
 * THE FOUR THINGS A FEATURE INSTANCE MUST HONOUR
 *
 * 1. DOCUMENTS LIVE IN THE WORKER. `RunContext` is all functions and cannot cross
 *    `postMessage`, so it is constructed inside the worker and never leaves. The main thread
 *    holds `DocumentId`s and view-sized projections. Measured on this project: one
 *    `structuredClone` of a 40,000-cue document costs 1.2 s coalesced and 2.5 s with word
 *    runs, on a desktop. Per slider tick, that is the whole interaction budget.
 *
 * 2. BYTES MOVE BY TRANSFER, NOT BY CLONE. Input bytes in and `Uint8Array` output out both
 *    transfer. THE SENDER'S VIEW DETACHES — read what you need from it FIRST. This is the
 *    difference between a 25 MB copy and a pointer move on every parse.
 *
 * 3. `failure` AND `crash` ARE NEVER MERGED. A `ParseResult` of `needs-framerate` is the
 *    MicroDVD FPS prompt, which is a feature. An exception is a defect and is always
 *    reported as one.
 *
 * 4. CANCELLATION IS COOPERATIVE AND CHUNKED. See `CHUNK` below for the unit boundaries the
 *    worker drains its queue at, and the latency each one costs.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import type {
  Diagnostic,
  DocDiff,
  DocumentId,
  EncodingInfo,
  Framerate,
  InputId,
  LossEntry,
  Op,
  ProjectionOptions,
  QcReport,
  QcSettings,
  ReadFormat,
  ResegmentOp,
  Ticks,
  TrackId,
  WriteFormat,
  WriteOptions,
} from '../../contracts/cue-model.ts';
// Type-only, and one-way: `src/core/registry/**` imports nothing from this file, so naming the
// verdict shape here cannot close a cycle.
import type { FormatAvailability } from './registry/pipeline.ts';
// Type-only, and one-way for the same reason: `src/editor/**` never imports this file.
import type { EditorAction, EditorPayload } from '../editor/session.ts';

/*───────────────────────────────────────────────────────────────────────────────────────
  THE CHUNK / CANCEL CONTRACT

  There is no `SharedArrayBuffer`: it needs cross-origin isolation, which needs
  `COEP: require-corp`, which breaks AdSense (a P0 launch requirement), and the escape hatch
  `COEP: credentialless` is unsupported in Safari. Isolation is OFF, permanently.

  So `ctx.signal.cancelled` can only change when the worker RETURNS TO ITS EVENT LOOP. The
  worker therefore drains its message queue at the unit boundaries below, and the worst-case
  cancel latency is the time one unit takes.

  1.2.0 — THE OP IS THE SMALLEST CANCELLABLE UNIT. THERE IS NO SMALLER ONE.

  `Transform` is SYNCHRONOUS by signature: it returns `TransformResult`, not a Promise. So a
  transform cannot await, so the worker cannot return to its event loop while one runs, so a
  queued `cancel` is never delivered, so `signal.cancelled` CANNOT CHANGE between the first
  statement of a transform and its last. Polling it every N cues inside one is not a cheap
  win — it reads the same value every time. Three instances wrote that loop because the
  earlier wording of this block invited it.

  What replaced it:
    - CANCEL between ops. Real, and the worker now honours it: `runPipelineYielding` drains
      the queue between ops, where a synchronous transform's boundary genuinely is.
    - PROGRESS inside one op. `ctx.onProgress` is just `postMessage`, which queues on the
      MAIN thread's loop and so does NOT need the worker to yield. It works from inside a
      synchronous transform, which is why CHUNK.CUES survives as the progress interval.

  Both directions are pinned by test/core/worker/sync-cancel.test.mjs.

  If one op over one document is genuinely too long to sit through, the mechanism is
  `worker.terminate()` and respawn — see TERMINATE_IS_THE_ONLY_CANCEL_FOR below. There is no
  third option, and inventing one in a feature module produces dead code, not cancellation.
───────────────────────────────────────────────────────────────────────────────────────*/

export const CHUNK = {
  /**
   * Cues between `ctx.onProgress` calls inside one op. Sized so a 40,000-cue document
   * reports ~40 times: often enough that the bar moves, rarely enough to be free.
   *
   * 1.2.0 — THIS IS A PROGRESS INTERVAL, NOT A CANCEL INTERVAL. It used to be documented as
   * "cues processed between queue drains inside one op", which a synchronous transform
   * cannot do. Polling `ctx.signal.cancelled` on this boundary is dead code: see the block
   * above. The VALUE is unchanged, so no caller's arithmetic moves.
   */
  CUES: 1000,
  /** Files processed between drains in a batch. One file is already a natural unit. */
  BATCH_FILES: 1,
  /** Ops in a pipeline between drains. Ops are coarse; every one is a boundary — and as of
   *  1.2.0 the worker actually drains there, via `runPipelineYielding`. Before that this
   *  constant described an intention nothing implemented: `apply` ran its whole op list in
   *  one synchronous stretch, so a twelve-op preset ignored Cancel until the last op. */
  PIPELINE_OPS: 1,
  /** ZIP entries between drains while expanding an archive. */
  ZIP_ENTRIES: 1,
} as const;

/**
 * The indivisible case, spelled out: ONE OP over one document. Nothing can interrupt it —
 * `Transform` is synchronous, so there is no point inside it at which the worker reads a
 * message — so the only mechanism is `worker.terminate()` and respawn, which is why the
 * allocator seed lives on the main thread (§4).
 *
 * 1.2.0 — this used to say "one op over one document, INSIDE ONE CHUNK", which implied a
 * sub-op boundary existed. None does.
 */
export const TERMINATE_IS_THE_ONLY_CANCEL_FOR =
  'a single op over a single document, e.g. a six-hour ASS → PDF render';

/*───────────────────────────────────────────────────────────────────────────────────────
  Main → Worker
───────────────────────────────────────────────────────────────────────────────────────*/

export interface InitMessage {
  readonly kind: 'init';
  readonly reqId: 0;
  /** Minted once per PAGE. Passed back on every respawn. */
  readonly epoch: string;
  /** The maximum counter the main thread has seen. A respawn resumes from it. */
  readonly allocatorCounter: number;
  readonly widthModel?: {
    readonly fullWidthCost: number;
    readonly halfWidthCost: number;
    readonly combiningCost: number;
    readonly emojiCost: number;
  };
  /** false in a production build: INVARIANTS are asserted at pipeline boundaries in dev only. */
  readonly devAssertions?: boolean;
}

export interface ParseMessage {
  readonly kind: 'parse';
  readonly reqId: number;
  /**
   * C12 — there is NO `inputId` here, and that is the point. **The worker is the sole id
   * authority.** It was safe for the main thread to mint one only while no reachable worker
   * path called `alloc.input()`; transcript ingestion calls it, so both sides would have been
   * allocating in the same `i` space one round later.
   *
   * Correlation is `reqId`. The minted `inputId` comes back on the result, and every message
   * that names an input afterwards (`redecode`, `dispose`) uses the id the worker returned.
   */
  readonly inputName: string;
  /** TRANSFERRED. The sender's view is detached afterwards. */
  readonly bytes: Uint8Array;
  readonly format?: ReadFormat;
  readonly encodingOverride?: string;
  readonly framerate?: Framerate;
  readonly retainRaw: boolean;
  /**
   * Keep the source bytes so `redecode` can re-render.
   * true  — an interactive single file, and the currently-previewed file of a batch
   * false — every other file in a batch. A 400-file ZIP OOMs at parse peak.
   */
  readonly retainBytes: boolean;
  /**
   * DOCUMENT MODE (SEO_BRIEF_08 section 7.2, /txt-to-pdf and /text-to-pdf): the decoded text is
   * read as paragraphs (`src/formats/document/text-document.ts`) instead of by a subtitle
   * parser, so nothing is timed or split. The bytes are decoded exactly as for any parse, with
   * the same detection and `encodingOverride`, and `redecode` keeps the mode. Absent everywhere
   * else, so every other parse is unchanged.
   */
  readonly document?: boolean;
}

export interface RedecodeMessage {
  readonly kind: 'redecode';
  readonly reqId: number;
  /** The id the WORKER returned from `parse`. Requires `retainBytes` on that parse. */
  readonly inputId: InputId;
  readonly encodingLabel: string;
}

/**
 * C1 — THE TRANSCRIPT ENTRY POINT. Without it nothing in `RequestKind` could accept a
 * `contracts/transcript-wire.md` document, so `/t/{id}/{format}` could not produce bytes and
 * every download chip on the transcript page led nowhere.
 *
 * The message carries the WIRE JSON and **the worker does both steps** — `wireToTranscript`
 * and `documentFromTranscript` — returning a `DocumentId` the rest of the protocol already
 * understands. Deliberately not "the main thread builds a `Transcript` and hands it over":
 * `wireToTranscript` MINTS an `inputId` and a `trackId`, and minting outside the worker is
 * exactly the second id authority C12 removes.
 *
 * From there the ordinary path applies: `resegment` turns segments into cues, `apply` runs
 * ops, `write` produces bytes. Segments are NOT cues, so this returns a document with ZERO of
 * them — see `cueCount` below.
 */
export interface IngestTranscriptMessage {
  readonly kind: 'ingestTranscript';
  readonly reqId: number;
  /**
   * The wire document, verbatim, as `contracts/transcript-wire.md` §2 specifies it —
   * snake_case, parsed from JSON but otherwise untouched. Plain JSON, so it structured-clones
   * with no transfer list.
   */
  readonly wire: unknown;
  /** Becomes `InputRef.name` and the default export basename. §10 slugifies it. */
  readonly inputName: string;
}

export interface ApplyMessage {
  readonly kind: 'apply';
  readonly reqId: number;
  readonly docId: DocumentId;
  /** Plain JSON by contract — no live RegExp, no Map, no closures. */
  readonly ops: readonly Op[];
}

export interface ResegmentMessage {
  readonly kind: 'resegment';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly op: ResegmentOp;
}

export interface ProjectMessage {
  readonly kind: 'project';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly format: WriteFormat;
  readonly options: ProjectionOptions;
}

export interface WriteMessage {
  readonly kind: 'write';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly format: WriteFormat;
  readonly options: WriteOptions;
}

/**
 * What every target would cost, BEFORE the user picks one.
 *
 * `availability()` was built in S21 and could not be called: it takes a `SubtitleDocument`, the
 * document lives in the worker, and the main thread holds only a `DocumentId`. Built, correct,
 * and on the wrong side of a boundary with no door in it. This is the door.
 *
 * NOT `WriteOptions`, though the brief said so. `availability()` takes
 * `Omit<WriteOptions, 'format'>` because the format is what VARIES across targets — one per
 * row. A full `WriteOptions` here would carry a `format` field that means nothing, and this
 * protocol has already paid twice for a format field that two sides read differently (F7, and
 * the write/project disagreement the handlers now refuse outright). The type says what is true.
 */
export interface AvailabilityMessage {
  readonly kind: 'availability';
  readonly reqId: number;
  readonly docId: DocumentId;
  /** Which formats to report on. Required — a silent "all" would hide a caller's mistake. */
  readonly targets: readonly WriteFormat[];
  readonly options: Omit<WriteOptions, 'format'>;
}

export interface DiffMessage {
  readonly kind: 'diff';
  readonly reqId: number;
  readonly docId: DocumentId;
  /** The rev of the baseline snapshot the worker holds for this document. */
  readonly baselineRev: number;
  readonly opts?: {
    readonly countsOnly?: boolean;
    readonly window?: { readonly from: Ticks; readonly to: Ticks };
  };
}

export interface QcMessage {
  readonly kind: 'qc';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly settings: QcSettings;
}

/** The cue window the UI is showing. THE mechanism that keeps documents in the worker. */
export interface PreviewMessage {
  readonly kind: 'preview';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly from: number;
  readonly count: number;
}

/**
 * The subtitle editor (cue-model 1.10.0, adapter.md 1.5.0): one edit, an undo, a redo, or a
 * question about where a cue is, against a document the worker holds.
 *
 * WHY A MESSAGE AND NOT AN `Op`. An edit names one cue by id, is never replayed from the file
 * as dropped, and needs undo back to the file as opened. `apply` replaces a document under its
 * own id and keeps no history, so undo had no door; this is it. The history lives beside the
 * document (src/editor/session.ts), the document is replaced under the SAME id after every edit
 * and every undo, and `preview` and `write` read it there as they always have.
 *
 * Edits are serialised per document behind the same lock as `apply`.
 */
export interface EditorMessage {
  readonly kind: 'editor';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly action: EditorAction;
}

/**
 * THE SUBTITLE TRANSLATOR, OUT (SEO_BRIEF_09 section 2.3, adapter.md §3.5): every cue's text with
 * its inline styling as tags, in document order, for the page to send to the language model.
 * `preview` gives plain text, which would drop the italics the brief says stay on their lines.
 */
export interface CueTextsMessage {
  readonly kind: 'cueTexts';
  readonly reqId: number;
  readonly docId: DocumentId;
}

/**
 * THE SUBTITLE TRANSLATOR, IN: translated text back onto many cues at once, by the position
 * `cueTexts` numbered each cue with (`i`), so it lands on a fresh copy of the same file (a
 * `redecode`) as well as on the document it was read from. Timing, position, style, actor and
 * layer are kept; raw passthrough goes stale; every track a translated cue is on says
 * `language`.
 *
 * `pair: true` leaves the document as it is and answers with two NEW documents, the original and
 * its translation, cue ids chosen so `mergeDocuments` stacks each cue with its own translation
 * (adapter.md §3.5): the two-language file. Its tracks keep the original's language.
 */
export interface TranslateCuesMessage {
  readonly kind: 'translateCues';
  readonly reqId: number;
  readonly docId: DocumentId;
  readonly texts: readonly { readonly i: number; readonly text: string }[];
  /** BCP-47, the target: what each translated cue's track says it is in. */
  readonly language: string;
  readonly pair?: boolean;
}

export interface BatchItem {
  /** C12: no `inputId` — the worker mints one per item, and per expanded archive entry. */
  readonly inputName: string;
  /** TRANSFERRED, like `parse`. */
  readonly bytes: Uint8Array;
  readonly format?: ReadFormat;
  readonly framerate?: Framerate;
}

export interface BatchMessage {
  readonly kind: 'batch';
  readonly reqId: number;
  readonly items: readonly BatchItem[];
  readonly ops: readonly Op[];
  readonly format: WriteFormat;
  readonly options: WriteOptions;
  /** Expand any item that is itself a ZIP. Default true. */
  readonly expandArchives?: boolean;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Video files — SEO_BRIEF_04 item 7a: the subtitle tracks inside an MKV/WebM or MP4/MOV

  Three requests, each stateless: the worker keeps no video between them. A 2 GB file held in
  the worker for the life of the tab, against a track list the page already has, is the wrong
  trade; reading it again when the user picks a second track is the cheap one.

    probeContainer  the track list, from the HEAD of the file where possible
    extractTrack    one TEXT track, reassembled and parsed: answers exactly like `parse`
    imageTrack      one IMAGE track, rewritten as the .sup or .idx/.sub the OCR feature reads
───────────────────────────────────────────────────────────────────────────────────────*/

export interface ProbeContainerMessage {
  readonly kind: 'probeContainer';
  readonly reqId: number;
  readonly inputName: string;
  /** TRANSFERRED. The whole file, or only its start (`partial`). */
  readonly bytes: Uint8Array;
  /**
   * `bytes` is the start of a larger file. An `incomplete` failure then means "the track list
   * is further in; read more", rather than a fault in the file.
   */
  readonly partial: boolean;
}

export interface ExtractTrackMessage {
  readonly kind: 'extractTrack';
  readonly reqId: number;
  /** The video's name. It names the download, as a dropped file's name does. */
  readonly inputName: string;
  /** TRANSFERRED. The WHOLE file: a Matroska track's blocks are spread through all of it. */
  readonly bytes: Uint8Array;
  /** `ContainerTrackInfo.index`, the container's own number for the track. */
  readonly trackIndex: number;
  readonly retainRaw: boolean;
  /**
   * As on `parse`. What is retained is the REASSEMBLED subtitle file (kilobytes), never the
   * video: redecode and every fresh copy re-read that.
   */
  readonly retainBytes: boolean;
}

export interface ImageTrackMessage {
  readonly kind: 'imageTrack';
  readonly reqId: number;
  readonly inputName: string;
  /** TRANSFERRED. The whole file. */
  readonly bytes: Uint8Array;
  readonly trackIndex: number;
}

export interface FontsMessage {
  readonly kind: 'fonts';
  readonly reqId: 0;
  readonly fonts: readonly {
    readonly family: string;
    readonly weight?: number;
    readonly italic?: boolean;
    readonly scripts?: readonly string[];
    /** TRANSFERRED. */
    readonly bytes: Uint8Array;
  }[];
}

/**
 * The pinyin table: several hundred KB, lazy-loaded on first PinyinOp, never bundled.
 *
 * CONTRACT NOTE: adapter.md §3's message table has no kind for this. §9 requires the table to
 * be "lazy-loaded on first PinyinOp" and RunContext declares the injection point, but nothing
 * carries the bytes across the boundary — so PinyinOp is unreachable without it. Raised as a
 * CONTRACT item in comms/CORE.md rather than added quietly; the shape here is the proposal.
 */
export interface PinyinMessage {
  readonly kind: 'pinyin';
  readonly reqId: 0;
  /** Hanzi → readings. `sourceLength` covers polyphones and 儿化, which are not 1:1. */
  readonly table: Readonly<
    Record<
      string,
      readonly { readonly text: string; readonly tone: number; readonly sourceLength: number }[]
    >
  >;
}

export interface CancelMessage {
  readonly kind: 'cancel';
  readonly reqId: 0;
  /** The reqId being cancelled, or 'all'. */
  readonly target: number | 'all';
}

export interface DisposeMessage {
  readonly kind: 'dispose';
  readonly reqId: 0;
  readonly docIds?: readonly DocumentId[];
  readonly inputIds?: readonly InputId[];
}

export type MainToWorker =
  | InitMessage
  | ParseMessage
  | RedecodeMessage
  | IngestTranscriptMessage
  | ApplyMessage
  | ResegmentMessage
  | ProjectMessage
  | WriteMessage
  | AvailabilityMessage
  | DiffMessage
  | QcMessage
  | PreviewMessage
  | BatchMessage
  | EditorMessage
  | CueTextsMessage
  | TranslateCuesMessage
  | ProbeContainerMessage
  | ExtractTrackMessage
  | ImageTrackMessage
  | FontsMessage
  | PinyinMessage
  | CancelMessage
  | DisposeMessage;

/*───────────────────────────────────────────────────────────────────────────────────────
  Worker → Main
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * The handshake. The main thread CHECKS these against its own build: a cached worker bundle
 * against a fresh page bundle is a real deployment state and it must fail LOUDLY, not
 * produce documents in a shape the page cannot read.
 */
export interface ReadyMessage {
  readonly kind: 'ready';
  readonly reqId: 0;
  readonly contractVersion: string;
  readonly schemaVersion: number;
}

export interface ResultMessage<V = unknown> {
  readonly kind: 'result';
  readonly reqId: number;
  readonly value: V;
  /** Returned on EVERY result. The main thread keeps the maximum and passes it back in `init`. */
  readonly allocatorCounter: number;
  readonly diagnostics: readonly Diagnostic[];
}

export interface ProgressMessage {
  readonly kind: 'progress';
  readonly reqId: number;
  readonly done: number;
  readonly total: number;
  readonly phase: string;
}

/** A TYPED library failure: `ParseResult` or `WriteFailure`. A feature, not a defect. */
export interface FailureMessage {
  readonly kind: 'failure';
  readonly reqId: number;
  readonly reason: string;
  readonly detail: unknown;
}

/** An actual exception. A BUG, always reported as one. Never merged with `failure`. */
export interface CrashMessage {
  readonly kind: 'crash';
  readonly reqId: number;
  readonly message: string;
  readonly stack?: string;
}

export type WorkerToMain =
  | ReadyMessage
  | ResultMessage
  | ProgressMessage
  | FailureMessage
  | CrashMessage;

/*───────────────────────────────────────────────────────────────────────────────────────
  Result payloads — what `value` actually is, per request kind
───────────────────────────────────────────────────────────────────────────────────────*/

export interface ParsedInfo {
  readonly docId: DocumentId;
  /** MINTED IN THE WORKER (C12) — this is where the main thread learns it. */
  readonly inputId: InputId;
  readonly format: ReadFormat;
  readonly cueCount: number;
  readonly encoding: EncodingInfo;
  /** True when the detector's confidence is below 0.7: the UI renders the override OPENED. */
  readonly needsEncodingOverride: boolean;
  readonly framerate: Framerate | null;
  readonly framerateSource: string | null;
  readonly durationTicks: Ticks | null;
  readonly tracks: readonly {
    readonly id: TrackId;
    readonly name: string;
    readonly language: string | null;
  }[];
  readonly loss: readonly LossEntry[];
  /** A byte length the UI can warn on (a *warning* over ~10 MB, never a cap). */
  readonly byteLength: number;
}

/** One row of the virtualised cue table, sized for a window rather than a document. */
export interface CuePreviewRow {
  readonly id: string;
  readonly index: number | null;
  readonly startTicks: number;
  readonly endTicks: number;
  readonly text: string;
  readonly lineCount: number;
  readonly trackId: TrackId;
  readonly layer: number;
}

export interface PreviewPayload {
  readonly docId: DocumentId;
  readonly from: number;
  readonly total: number;
  readonly rows: readonly CuePreviewRow[];
}

/**
 * C1's result. Deliberately richer than `{docId}`: the transcript page renders from this
 * without a second round trip, and `wordTiming` in particular decides which export formats
 * are offered at all.
 */
export interface TranscriptIngested {
  readonly docId: DocumentId;
  /** MINTED IN THE WORKER (C12). Use these for any later message that names them. */
  readonly inputId: InputId;
  readonly trackId: TrackId;
  /** Segments are model artefacts, not caption lines. */
  readonly segmentCount: number;
  /**
   * ZERO at ingestion, always. `documentFromTranscript` produces a complete document with no
   * cues; cues appear when `resegment` runs. Returned anyway so a caller cannot mistake the
   * absence of the field for the absence of the guarantee.
   */
  readonly cueCount: 0;
  /**
   * false ⇒ the per-word path is unavailable for this transcript, so Enhanced LRC, ASS
   * karaoke and word-level VTT must be disabled rather than offered and then failing. Learn
   * it here rather than re-deriving it from the segments.
   */
  readonly wordTiming: boolean;
  /** BCP-47, never null — 'und' when unknown, paired with probability 0. */
  readonly language: string;
  /** A DECLARED caption-track language arrives as 1.0; it was not guessed. */
  readonly languageProbability: number;
  /** From the metadata probe, for the duration line. null when the source gave none. */
  readonly durationTicks: Ticks | null;
  /** The source title, which §10 slugifies into the default export filename. */
  readonly title: string | null;
}

export interface ProjectPayload {
  readonly loss: readonly LossEntry[];
  readonly blocking: readonly LossEntry[];
  readonly cueCount: number;
}

export interface WritePayload {
  readonly filename: string;
  /** TRANSFERRED when it is a `Uint8Array`. */
  readonly output: string | Uint8Array;
  readonly loss: readonly LossEntry[];
}

/** Per-file status, streamed to the UI AS IT GOES. One malformed file in forty must not
 *  fail the set, and the archive contains everything that worked. */
export interface BatchItemStatus {
  readonly inputName: string;
  readonly status: 'converted' | 'skipped';
  /** Present only when `status` is 'skipped'. Machine-readable, so the UI groups them. */
  readonly reason?: string;
  readonly detail?: string;
  readonly outputName?: string;
}

export interface BatchPayload {
  readonly items: readonly BatchItemStatus[];
  readonly converted: number;
  readonly skipped: number;
  readonly archiveName: string;
  /** TRANSFERRED. Valid even when the batch was cancelled part-way. */
  readonly archive: Uint8Array;
  readonly cancelled: boolean;
}

/** One subtitle track inside a video, classified by CONVERT from its codec id. */
export interface ContainerTrackInfo {
  /** The container's own track number (MKV) or track id (MP4). Send it back as `trackIndex`. */
  readonly index: number;
  /** The container's codec id, verbatim: `S_TEXT/ASS`, `tx3g`, `S_HDMV/PGS`. */
  readonly codec: string;
  /** The codec as a person would say it, for the picker: "ASS", "PGS (Blu-ray)". */
  readonly codecLabel: string;
  readonly language: string | null;
  readonly title: string | null;
  /** text: a reader takes it. image: pictures of text, for OCR. unsupported: listed, declined. */
  readonly kind: 'text' | 'image' | 'unsupported';
  /** The reader for a text track; null otherwise. */
  readonly format: ReadFormat | null;
  /** The picture family for an image track; null otherwise. */
  readonly family: 'pgs' | 'vobsub' | 'dvb' | null;
}

export interface ContainerProbePayload {
  readonly container: 'matroska' | 'mp4';
  /** Every subtitle track, in file order. EMPTY means the video carries none. */
  readonly tracks: readonly ContainerTrackInfo[];
}

/**
 * `extractTrack`'s answer: everything `parse` returns for a dropped file, plus the track it
 * came from and what the user should know about it (an edit list not applied, cues with no end
 * time). The warnings are sentences for a person, not `Diagnostic`s: none of them is a
 * cue-model finding, and the code union is the contract's.
 */
export interface ExtractedTrackInfo extends ParsedInfo {
  readonly track: ContainerTrackInfo;
  readonly warnings: readonly string[];
}

/** An image track as the files a dropped one would be: `.sup`, or `.idx` and `.sub`. */
export interface ImageTrackPayload {
  readonly family: 'pgs' | 'vobsub';
  /** TRANSFERRED. In the order a reader wants them: the `.idx` before its `.sub`. */
  readonly files: readonly { readonly name: string; readonly bytes: Uint8Array }[];
  readonly pictureCount: number;
  readonly warnings: readonly string[];
}

/**
 * Why a cue's text is not sent: no words (`empty`, a drawing on its own too), never on screen
 * (`comment`), a vector drawing among its words (`drawing`), animations or unclassified tags the fragile-tag gate
 * protects (`fragile`), or inline styling the tags cannot carry (`styled`). adapter.md §3.5.
 */
export type CueTextSkip = 'empty' | 'comment' | 'drawing' | 'fragile' | 'styled';

/** One cue, out: `text` with its tags (lines joined by '\n'), or null and `skip`. */
export interface CueTextRow {
  /** Position in the document: `TranslateCuesMessage.texts[].i`. */
  readonly i: number;
  readonly id: string;
  readonly trackId: TrackId;
  readonly text: string | null;
  readonly skip: CueTextSkip | null;
}

export interface CueTextsPayload {
  readonly docId: DocumentId;
  readonly cues: readonly CueTextRow[];
}

export interface TranslatedCuesPayload {
  /** The document now holding the translation: the one sent, or with `pair` the new original. */
  readonly docId: DocumentId;
  /** `pair` only: the translation, for `MergeDocumentsOp.otherId`. Null otherwise. */
  readonly translationId: DocumentId | null;
  /** Cues whose lines were replaced. A cue the tags could not carry, or an empty text, is not. */
  readonly translated: number;
  readonly cueCount: number;
}

export type DiffPayload = DocDiff;
export type QcPayload = QcReport;

/** One verdict per requested target, in the order asked. */
export interface AvailabilityPayload {
  readonly formats: readonly FormatAvailability[];
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Type-safe request/response pairing
───────────────────────────────────────────────────────────────────────────────────────*/

export interface ResultTypes {
  parse: ParsedInfo;
  redecode: ParsedInfo;
  ingestTranscript: TranscriptIngested;
  apply: {
    readonly docId: DocumentId;
    readonly touched: number;
    readonly cueCount: number;
    readonly loss: readonly LossEntry[];
    readonly spawned: readonly DocumentId[];
  };
  resegment: { readonly docId: DocumentId; readonly cueCount: number };
  project: ProjectPayload;
  write: WritePayload;
  availability: AvailabilityPayload;
  diff: DiffPayload;
  qc: QcPayload;
  preview: PreviewPayload;
  batch: BatchPayload;
  editor: EditorPayload;
  cueTexts: CueTextsPayload;
  translateCues: TranslatedCuesPayload;
  probeContainer: ContainerProbePayload;
  /** A text track, reassembled and parsed: the same answer `parse` gives a dropped file. */
  extractTrack: ExtractedTrackInfo;
  imageTrack: ImageTrackPayload;
}

export type RequestKind = keyof ResultTypes;

/** Kinds that carry `reqId: 0` and never get a `result`. */
export const FIRE_AND_FORGET = ['init', 'fonts', 'pinyin', 'cancel', 'dispose'] as const;

export function isFireAndForget(kind: MainToWorker['kind']): boolean {
  return (FIRE_AND_FORGET as readonly string[]).includes(kind);
}

/**
 * Every `Uint8Array` in a message, for the `transfer` list. Kept here so both sides derive
 * the list the same way and nobody forgets the one inside `batch.items[n].bytes`.
 */
export function transferablesOf(message: MainToWorker): ArrayBufferLike[] {
  const out: ArrayBufferLike[] = [];
  if (message.kind === 'parse') out.push(message.bytes.buffer);
  else if (message.kind === 'batch') for (const item of message.items) out.push(item.bytes.buffer);
  else if (message.kind === 'fonts') for (const f of message.fonts) out.push(f.bytes.buffer);
  else if (
    message.kind === 'probeContainer' ||
    message.kind === 'extractTrack' ||
    message.kind === 'imageTrack'
  ) {
    // A video. Cloned rather than moved, it would be a second copy of up to gigabytes.
    out.push(message.bytes.buffer);
  }
  return out;
}

export function resultTransferablesOf(message: WorkerToMain): ArrayBufferLike[] {
  if (message.kind !== 'result') return [];
  const value = message.value as { output?: unknown; archive?: unknown; files?: unknown };
  const out: ArrayBufferLike[] = [];
  if (value.output instanceof Uint8Array) out.push(value.output.buffer);
  if (value.archive instanceof Uint8Array) out.push(value.archive.buffer);
  if (Array.isArray(value.files)) {
    for (const f of value.files as { bytes?: unknown }[]) {
      // Distinct buffers only: listing one twice makes postMessage throw.
      if (f.bytes instanceof Uint8Array && !out.includes(f.bytes.buffer)) out.push(f.bytes.buffer);
    }
  }
  return out;
}
